import {
  IconAlert,
  IconClipboard,
  IconDevice,
  IconPulse,
  IconWallet,
  KvRow,
  Loading,
  MetricCard,
  Notice,
  PageHeader,
  Section,
  StatusChip,
  EmptyState,
} from '@didto/ui';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useSession } from '@/app/session';
import { useClock } from '@/app/useClock';
import { useSetting } from '@/app/useSetting';
import { health } from '@/lib/api';
import { displayBusinessName } from '@/lib/businessName';
import { DEVICE_STATE, deviceState } from '@/lib/deviceState';
import { supabase } from '@/lib/supabase';
import { env } from '@/lib/env';
import { useReviewCounts } from '@/lib/review';
import { ReadError } from './shifts/reviewItems';
import './shifts/shifts.css';

/** The overview: what is to review first (the owner's), then the devices and the system. */
export function DashboardPage() {
  const s = useSession();
  const h = useQuery({ queryKey: ['health'], queryFn: health, refetchInterval: 60_000 });
  const terminals = useQuery({
    queryKey: ['terminals-dashboard'],
    enabled: s.can('device.view'),
    refetchInterval: 30_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('terminal')
        .select('id, name, code, kind, status, last_seen_at, last_app_version')
        .is('archived_at', null)
        .order('code');
      if (error) throw error;
      return data;
    },
  });
  const threshold = Number(useSetting('terminal.online_threshold_seconds'));
  const reviewLocation = s.me?.active_location_id ?? s.me?.locations[0]?.id ?? null;
  const canReview = s.me?.principal === 'human' && s.can('shift.review');
  const review = useReviewCounts(reviewLocation, canReview);
  const counts = review.data;
  const now = useClock(15_000);

  const red = counts?.red_open ?? 0;

  return (
    <div className="d-page">
      <PageHeader
        eyebrow={displayBusinessName(s.me?.business?.name) || 'didto. OS'}
        title="Dashboard"
        subtitle="What needs you first, then the devices and the system."
      />
      {s.me?.principal === 'human' && !s.me.mfa_enrolled && (
        <Notice
          tone="warning"
          role="status"
          testId="mfa-nudge"
          title="Two-step verification is not enabled for your login"
          action={
            <Link to="/settings/security" className="d-btn d-btn--primary d-btn--sm">
              Enable now
            </Link>
          }
        >
          Owner and manager logins should use an authenticator app. It takes one minute.
        </Notice>
      )}
      {canReview && (
        <Section
          title="To review"
          description="The owner's review of the last 31 days."
          actions={
            <Link to="/shifts" className="d-btn d-btn--sm d-btn--ghost">
              Shifts
            </Link>
          }
        >
          <ReadError
            error={review.error}
            hasData={!!counts}
            onRetry={() => void review.refetch()}
          />
          <div className="d-metrics" data-testid="dashboard-review">
            {/* the figure opens the review queue; the whole card is its target */}
            <div className={`d-metric rv-tile${red > 0 ? ' d-metric--danger' : ''}`}>
              <div className="d-metric__head">
                <span className="d-metric__icon" aria-hidden>
                  <IconAlert />
                </span>
                <span className="d-metric__label">To check</span>
              </div>
              <div className="d-metric__value">
                <Link
                  to="/shifts/review"
                  className="rv-tile__link"
                  aria-label={counts ? `${red} to check` : 'To check'}
                  data-testid="dashboard-review-red"
                >
                  {counts?.red_open ?? '—'}
                </Link>
              </div>
              <div className="d-metric__note">
                {!counts ? '\u00a0' : red > 0 ? 'Waiting for your answer' : 'Nothing waiting'}
              </div>
            </div>
            <MetricCard
              icon={<IconClipboard />}
              label="Shifts not reviewed"
              value={counts?.unreviewed_shifts ?? '—'}
              tone={counts && counts.unreviewed_shifts > 0 ? 'warning' : undefined}
              note={
                counts?.oldest_unreviewed_date
                  ? `Oldest ${counts.oldest_unreviewed_date}`
                  : undefined
              }
            />
            <MetricCard
              icon={<IconWallet />}
              label="Non-cash to check"
              value={counts?.unmarked_rows ?? '—'}
              tone={counts && counts.unmarked_rows > 0 ? 'warning' : undefined}
              note="GCash and other non-cash payments"
            />
          </div>
        </Section>
      )}
      <div className="dash__grid">
        {s.can('device.view') && (
          <Section
            title="Devices"
            actions={
              <Link to="/devices" className="d-btn d-btn--sm d-btn--ghost">
                Manage
              </Link>
            }
          >
            <ReadError
              error={terminals.error}
              hasData={!!terminals.data}
              onRetry={() => void terminals.refetch()}
            />
            {terminals.isLoading && <Loading label="Loading the devices" rows={2} />}
            <div className="dash__list" data-testid="dashboard-devices">
              {(terminals.data ?? []).map((t) => {
                const st = DEVICE_STATE[deviceState(t, now, threshold)];
                return (
                  <div key={t.id} className="dash__item">
                    <span className="dash__icon" aria-hidden>
                      <IconDevice />
                    </span>
                    <div className="d-row__main">
                      <div className="d-row__title">
                        {t.code} · {t.name}
                      </div>
                      <div className="d-row__meta">
                        {KIND_LABEL[t.kind] ?? t.kind} · {t.last_app_version ?? 'never seen'}
                      </div>
                    </div>
                    <StatusChip tone={st.tone} shape={st.shape}>
                      {st.label}
                    </StatusChip>
                  </div>
                );
              })}
              {terminals.data?.length === 0 && <EmptyState title="No devices enrolled yet." />}
            </div>
          </Section>
        )}
        <Section title="System" actions={<IconPulse />}>
          <div className="d-kv">
            <KvRow
              label="Backend"
              value={
                h.isSuccess ? (
                  <StatusChip tone="success">healthy</StatusChip>
                ) : h.isError ? (
                  <StatusChip tone="danger" shape="square">
                    unreachable
                  </StatusChip>
                ) : (
                  <StatusChip>checking…</StatusChip>
                )
              }
            />
            <KvRow
              label="Schema"
              value={<span className="d-mono">{h.data?.schema_version ?? '—'}</span>}
            />
            <KvRow label="App" value={<span className="d-mono">v{env.appVersion}</span>} />
            <KvRow label="Location" value={s.me?.locations[0]?.name ?? '—'} />
          </div>
        </Section>
      </div>
    </div>
  );
}

const KIND_LABEL: Record<string, string> = {
  pos: 'POS',
  kds: 'Kitchen or bar',
  expo: 'Expo',
  admin: 'Office',
};
